"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";

import {
  BEHAVIOR_METRIC_KEYS,
  type ReplayMetricKey,
  type ReplayPlayerMetrics,
  formatMetricValue,
} from "@/entities/replay-metrics";
import { scoreColor } from "@/shared/lib/score-color";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

import { SectionHeading } from "./section-heading";
import { ProfilePlayerPicker, type PickerPlayer } from "./profile-player-picker";
import { ProfileRadar, type RadarAxis } from "./profile-radar";
import { ProfileGameBars, type ProfileGame } from "./profile-game-bars";
import { computeAxisRank } from "./lib/axis-rank";


/**
 * KAN-876: поведенческий профиль игрока (эпик KAN-804).
 *
 * Самая крупная секция макета и единственная, ради которой остальные поднимают
 * `onSelectPlayer`: пьедестал, карточка команды и таблица игроков выбирают человека
 * здесь. Поэтому выбранный игрок живёт НЕ здесь, а на странице — иначе четыре секции
 * спорили бы за то, кто показан.
 *
 * Три колонки: поиск, радар, таблица осей со столбиками по играм.
 */

function playerName(player: ReplayPlayerMetrics): string {
  return (
    (player.displayName ?? "").trim() ||
    (player.riotIdGameName ?? "").trim() ||
    "—"
  );
}

/**
 * KAN-900: объяснение метрики. `Popover`, а не `Tooltip`, намеренно: подсказка по
 * наведению на телефоне недоступна вовсе, а объяснение «что это за метрика» нужно
 * именно тому, кто видит её впервые.
 */
function AxisLabel({ label, explain }: { label: string; explain: string }) {
  /*
   * `pb-1` — условие видимости пунктира, а не отступ ради воздуха.
   *
   * `truncate` даёт `overflow: hidden`, а высота кнопки равна высоте строки (20px).
   * Пунктир стоит на 4px ниже базовой линии — ровно на границе обрезки, и попадает
   * внутрь или наружу по округлению. На проде из-за этого он был виден у трёх подписей
   * из семи, и выглядело это как «у части метрик нет описания», хотя описание есть у
   * всех семи и кнопка у всех кликается.
   */
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="max-w-full truncate pb-1 text-left text-sm text-foreground underline decoration-dotted decoration-muted-foreground underline-offset-4 transition-colors hover:text-primary"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-[300px] text-[13px] leading-snug">
        {explain}
      </PopoverContent>
    </Popover>
  );
}

export function PlayerProfile({
  players,
  teamNameByUserId,
  selectedUserId,
  onSelectPlayer,
  gamesByUserId,
}: {
  /** Все игроки турнира с метриками — они же выборка для поиска и для места в роли. */
  players: ReplayPlayerMetrics[];
  teamNameByUserId?: Record<string, string | null | undefined>;
  selectedUserId?: string | null;
  onSelectPlayer: (userId: string) => void;
  /**
   * Игры по игрокам. Карта, а не список выбранного: выбор меняется кликом, и
   * подтягивать игры отдельным запросом на каждый клик значило бы дёргать сеть там,
   * где всё уже пришло одним ответом.
   */
  gamesByUserId?: Map<string, ProfileGame[]>;
}) {
  const t = useTranslations("TournamentStats.playerProfile");
  const tm = useTranslations("Profile.behaviorMetrics");

  const pickerPlayers = useMemo<PickerPlayer[]>(
    () =>
      players.map((player) => ({
        player,
        teamName: player.userId ? teamNameByUserId?.[player.userId] : null,
      })),
    [players, teamNameByUserId],
  );

  /**
   * Никого не выбрали — показываем лучшего по баллу. Пустая секция на месте самой
   * крупной части страницы выглядит как поломка, а «лучший» — осмысленная точка
   * входа: именно его чаще всего и открывают первым.
   *
   * Если балла нет НИ У КОГО (турнир маленький, выборка не набралась), берём первого
   * попавшегося. Иначе секция исчезала бы целиком там, где показать есть что: сырые
   * значения метрик посчитаны и без перцентилей.
   */
  const selected = useMemo(() => {
    const byId = selectedUserId
      ? players.find((p) => p.userId === selectedUserId)
      : undefined;
    if (byId) return byId;

    const best = [...players]
      .filter((p) => p.glScore != null)
      .sort((a, b) => (b.glScore ?? 0) - (a.glScore ?? 0))[0];

    return best ?? players[0];
  }, [players, selectedUserId]);

  const axes = useMemo<RadarAxis[]>(() => {
    if (!selected) return [];

    return BEHAVIOR_METRIC_KEYS.map((key: ReplayMetricKey) => {
      const metric = selected.metrics.find((m) => m.key === key);
      const rank = computeAxisRank(players, selected, key);

      return {
        key,
        label: tm(`axis.${key}`),
        score: metric?.percentile != null ? Math.round(metric.percentile * 100) : null,
        raw:
          metric && metric.value !== null
            ? `${formatMetricValue(key, metric.value)}`
            : tm(
                metric?.availability === "Undefined"
                  ? "missing.undefined"
                  : "missing.noData",
              ),
        rankNote: rank ? t("rankNote", { rank: rank.rank, count: rank.peerCount }) : null,
      };
    });
  }, [players, selected, t, tm]);

  if (players.length === 0 || !selected) return null;

  const measured = axes.filter((a) => a.score != null).length;

  return (
    <section className="rounded-2xl border border-border bg-card p-[22px]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        {/* KAN-900: текстовое пояснение про роль и размер группы убрано по просьбе
            заказчика. Сам факт не потерян: размер группы виден в подписи места
            («2-й из 6») у каждой оси, а объяснение метрики — в подсказке на её
            названии. */}
        <SectionHeading title={t("title")} />

        <div className="flex items-center gap-[9px] rounded-[10px] border border-surface-inset-border bg-surface-inset px-[13px] py-[7px]">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            {t("selected")}
          </span>
          <span className="font-heading text-lg font-semibold leading-[1.1]">
            {playerName(selected)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-[18px]">
        <ProfilePlayerPicker
          players={pickerPlayers}
          selectedUserId={selected.userId}
          onSelect={onSelectPlayer}
        />

        {/* Ни одной измеренной оси — радара нет вовсе. Семиугольник из нулей
            выглядел бы как приговор игроку, хотя правда — «сравнивать не с чем». */}
        {measured > 0 ? (
          <ProfileRadar axes={axes} />
        ) : (
          <div className="min-w-0 max-w-[470px] flex-1 basis-[380px] rounded-[13px] border border-surface-inset-border bg-surface-inset px-4 py-6 text-[13px] text-muted-foreground">
            {tm("notEnoughForRadar")}
          </div>
        )}

        <div className="flex min-w-0 flex-1 basis-[360px] flex-col gap-5">
          <div className="flex flex-col gap-0.5">
            {axes.map((axis) => (
              <div
                key={axis.key}
                className="grid grid-cols-[minmax(0,150px)_minmax(60px,1fr)_52px_38px_auto] items-center gap-2.5 border-b border-border py-[9px]"
              >
                <AxisLabel label={axis.label} explain={tm(`explain.${axis.key}`)} />

                {/* Оси без балла шкала не рисуется и цвета не получает: длина полосы
                    это утверждение о месте внутри роли, а его здесь нет. */}
                <span className="relative block h-2 overflow-hidden rounded-full bg-muted">
                  {axis.score != null ? (
                    <span
                      className="block h-full rounded-full"
                      style={{
                        width: `${axis.score}%`,
                        background: scoreColor(axis.score),
                      }}
                    />
                  ) : null}
                </span>

                <span className="text-right text-[13px] font-semibold tabular-nums">
                  {axis.raw}
                </span>

                <span
                  className="text-right font-heading text-[17px] font-semibold tabular-nums"
                  style={axis.score != null ? { color: scoreColor(axis.score) } : undefined}
                >
                  {axis.score ?? "—"}
                </span>

                <span className="text-right text-[11px] text-muted-foreground">
                  {axis.rankNote ?? ""}
                </span>
              </div>
            ))}
          </div>

          <ProfileGameBars
            games={(selected.userId && gamesByUserId?.get(selected.userId)) || []}
          />
        </div>
      </div>
    </section>
  );
}
